import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search } from 'lucide-react';
import { getGlobalTasksArray } from '../../stores/globalTasksStore';
import { useTaskCategories } from '../../stores/hooks';
import type { TaskCategory } from '../../types';

export interface CanvasTaskPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (taskId: string) => void;
  existingTaskIds: Set<string>;
}

export function CanvasTaskPicker({ isOpen, onClose, onSelect, existingTaskIds }: CanvasTaskPickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const categories = useTaskCategories();

  // Read fresh on every render — this modal is short-lived (open/close)
  const allTasks = isOpen ? getGlobalTasksArray() : [];

  const filteredTasks = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return allTasks
      .filter(t => !existingTaskIds.has(t.id))
      .filter(t => !search || t.title.toLowerCase().includes(lowerSearch));
  }, [allTasks, existingTaskIds, search]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" onClick={onClose}>
      <div
        className="bg-white rounded-lg border border-gray-200 shadow-xl w-96 max-h-[60vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-800">{t('canvasPicker.addTask')}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={14} />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-gray-100">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('canvasPicker.searchTask')}
              className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-300"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredTasks.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-gray-400">
              {search ? t('canvasPicker.noTasksFound') : t('canvasPicker.allTasksInTree')}
            </div>
          ) : (
            filteredTasks.map(task => {
              const catDots = (task.categoryIds ?? [])
                .map(id => categories.find((c: TaskCategory) => c.id === id))
                .filter(Boolean) as TaskCategory[];

              return (
                <button
                  key={task.id}
                  onClick={() => { onSelect(task.id); setSearch(''); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                >
                  {catDots.length > 0 && (
                    <div className="flex gap-0.5 flex-shrink-0">
                      {catDots.map(cat => (
                        <div
                          key={cat.id}
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                      ))}
                    </div>
                  )}
                  <span className="text-xs text-gray-700 truncate">{task.title}</span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
